import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../common/Card';
import { Heading, Text } from '../common/Typography';
import { fetchPlayerRecentSessions } from '../../services/playerService';
import StatusBadge from '../common/StatusBadge';

/**
 * Recent Sessions Card - Shows the player's last 5 played sessions
 * Acts as a shortcut to view session performance
 */
const RecentSessionCard = ({ currentUser, compact = false }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadRecentSessions = async () => {
      if (!currentUser?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const recentSessions = await fetchPlayerRecentSessions(currentUser.id, 5);
        setSessions(recentSessions);
      } catch (err) {
        console.error('Error loading recent sessions:', err);
        setError('Failed to load recent sessions');
      } finally {
        setLoading(false);
      }
    };

    loadRecentSessions();
  }, [currentUser?.id]);

  const handleSessionClick = (session) => {
    if (!session || !session.clubId || !session.sessionId || !currentUser?.id) return;

    // Navigate to the session performance page
    navigate(`/player/${currentUser.id}/session/${session.sessionId}`, {
      state: { clubId: session.clubId }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[date.getDay()];
    const dateStr = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${dayName}, ${dateStr}`;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  // ─── Compact mode (hub overview): 2 dark rows + "View All" ────────────────
  if (compact) {
    if (loading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[0, 1].map(i => (
            <div key={i} className="h-16 rounded-xl bg-surface-subtle animate-pulse" />
          ))}
        </div>
      );
    }

    if (sessions.length === 0) {
      return (
        <div className="rounded-xl bg-surface-subtle border border-border px-4 py-5 text-center text-sm text-text-muted">
          No recent sessions yet — play some games to see your activity here.
        </div>
      );
    }

    const recent = [...sessions].reverse().slice(0, 2);

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {recent.map((session) => {
          const eloDelta = session.mixedEloDelta || session.levelsEloDelta || 0;
          const lg = session.lastGame;
          const hasScore = lg && (lg.myScore != null) && (lg.oppScore != null);
          const oppStr = lg?.opponents?.length ? lg.opponents.join(' & ') : null;

          return (
            <div
              key={`${session.clubId}-${session.sessionId}`}
              onClick={() => handleSessionClick(session)}
              className="flex items-center gap-3 rounded-xl bg-surface-subtle border border-border px-4 py-3.5 cursor-pointer transition-all hover:bg-surface hover:shadow-sm active:scale-[0.99]"
            >
              {/* Left: session name, club, last game + elo */}
              <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate">{session.sessionName}</p>
                <p className="text-xs text-text-muted truncate">{session.clubName}</p>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                  <p className="text-xs text-text-muted truncate">
                    {hasScore ? (
                      <>
                        <span className="text-text-muted">Last game: </span>
                        <span className={`font-semibold ${lg.won ? 'text-success' : 'text-danger'}`}>
                          {lg.myScore}–{lg.oppScore}
                        </span>
                        {oppStr && <span className="ml-1.5">vs {oppStr}</span>}
                      </>
                    ) : (
                      <span>{session.wins}W · {session.losses}L this session</span>
                    )}
                  </p>
                  <span className={`text-xs font-bold ${eloDelta > 0 ? 'text-success' : eloDelta < 0 ? 'text-danger' : 'text-text-muted'}`}>
                    {eloDelta > 0 ? '+' : ''}{eloDelta} Elo
                  </span>
                </div>
              </div>
              {/* Right: arrow centered vertically in card */}
              <div className="flex-shrink-0 flex items-center justify-center">
                <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ─── Standard mode (full card, horizontal scroll) ───────────────────────────
  if (loading) {
    return (
      <Card variant="default" size="large">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="default" size="large">
        <div className="text-center py-6">
          <Text className="text-text-secondary">{error}</Text>
        </div>
      </Card>
    );
  }

  if (sessions.length === 0) {
    return (
      <Card variant="default" size="large">
        <div className="text-center py-6">
          <Text className="text-text-secondary">No recent sessions found</Text>
          <Text className="text-text-muted text-sm mt-2">
            Play some games to see your session performance here
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="default" size="large">
      <div className="mb-4">
        <Heading level={3} className="text-lg font-semibold text-text-primary mb-1">
          Last 5 Sessions
        </Heading>
        <Text className="text-sm text-text-secondary">
          Tap any session to view full performance details
        </Text>
      </div>

      <div className="overflow-x-auto -mx-4 px-4">
        <div className="flex gap-4 pb-2">
          {[...sessions].reverse().map((session, index) => {
            const winRate = session.gamesPlayed > 0 
              ? ((session.wins / session.gamesPlayed) * 100).toFixed(0)
              : 0;
            const isLatest = index === 0;

            return (
              <div
                key={`${session.clubId}-${session.sessionId}`}
                className="flex-shrink-0 w-64 p-4 border border-border rounded-lg cursor-pointer transition-all hover:shadow-md hover:border-primary active:scale-[0.98] bg-surface"
                onClick={() => handleSessionClick(session)}
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="font-semibold text-text-primary text-sm truncate flex-1">
                      {session.sessionName}
                    </div>
                    {isLatest && (
                      <StatusBadge variant="info" size="small">Latest</StatusBadge>
                    )}
                  </div>
                  <div className="text-xs text-text-secondary mb-2 truncate">{session.clubName}</div>
                  {session.latestGameDate && (
                    <div className="text-xs text-text-muted mb-3">
                      {formatDate(session.latestGameDate)}
                      {formatTime(session.latestGameDate) && ` • ${formatTime(session.latestGameDate)}`}
                    </div>
                  )}
                  <div className="mt-auto space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-text-primary">
                        {session.wins}W / {session.losses}L
                      </span>
                      {session.gamesPlayed > 0 && (
                        <StatusBadge variant={winRate >= 50 ? 'success' : 'default'} size="small">
                          {winRate}%
                        </StatusBadge>
                      )}
                    </div>
                    <div className="text-xs space-y-0.5">
                      {session.mixedEloDelta !== 0 && (
                        <div>
                          <span className="text-text-muted">Mixed: </span>
                          <span className={session.mixedEloDelta >= 0 ? 'text-success font-medium' : 'text-danger font-medium'}>
                            {session.mixedEloDelta >= 0 ? '+' : ''}{session.mixedEloDelta}
                          </span>
                        </div>
                      )}
                      {session.levelsEloDelta !== 0 && (
                        <div>
                          <span className="text-text-muted">Levels: </span>
                          <span className={session.levelsEloDelta >= 0 ? 'text-success font-medium' : 'text-danger font-medium'}>
                            {session.levelsEloDelta >= 0 ? '+' : ''}{session.levelsEloDelta}
                          </span>
                        </div>
                      )}
                      {session.mixedEloDelta === 0 && session.levelsEloDelta === 0 && (
                        <span className="text-text-muted">No ELO change</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export default RecentSessionCard;

